import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Select, { StylesConfig } from 'react-select';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import CreditCardType from 'src/models/AP/CreditCardType';

export interface ICreditCardTransactionSearchCriteria {
    transactionNumber?: number;
    creditCardType?: string;
    checkDate?: Date;
    closeDate?: Date;
    invoiceDate?: Date;
    invoiceAmount?: number;
    invoiceAmountLike?: boolean;
    checkNumber?: number;
}

interface ICreditCardSearchProps {
    creditCardTypes?: Array<CreditCardType>;
    setIsCollapsed?: (isCollapsed: boolean) => void;
    runQuery?: (criteria: ICreditCardTransactionSearchCriteria) => void;
    size?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
}

interface IOptionType {
    value: string;
    label: string;
}

// All fields here are query-by-example criteria: typing/selecting only updates local
// component state. Nothing is looked up or fetched until the user clicks "Search".
const CreditCardSearch = (props: ICreditCardSearchProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isUserSearchingCreditCardType, setIsUserSearchingCreditCardType] = useState<boolean>(false);
    const [selectedCreditCardType, setSelectedCreditCardType] = useState<string>(undefined);

    const creditCardTypeOptions: IOptionType[] = props?.creditCardTypes?.map((c) => {
        return { value: `${c.id}`, label: c.name };
    }) ?? [];

    const customStyles: StylesConfig<IOptionType, false> = {
        placeholder: (provided) => ({
            ...provided,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '30px',
            height: '30px',
            padding: '0px',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0px 8px',
            height: '30px',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
            padding: '0px',
            whiteSpace: 'nowrap'
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '30px',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '0px',
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: '0px',
        }),
    };

    const toDate = (dateStr: string) => !!dateStr ? new Date(dateStr) : undefined;
    const toNumber = (numStr: string) => !!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined;

    const executeSearch = () => {
        const values = getValues();
        const criteria: ICreditCardTransactionSearchCriteria = {
            transactionNumber: toNumber(values.transactionNumberSearch),
            creditCardType: selectedCreditCardType,
            checkDate: toDate(values.checkDateSearch),
            closeDate: toDate(values.closeDateSearch),
            invoiceDate: toDate(values.invoiceDateSearch),
            invoiceAmount: toNumber(values.invoiceAmountSearch),
            invoiceAmountLike: !!values.invoiceAmountLike,
            checkNumber: toNumber(values.checkNumberSearch),
        };

        if (props?.runQuery) {
            props.runQuery(criteria);
        }
    };

    const clearSearchFilters = () => {
        setValue('transactionNumberSearch', '');
        setValue('checkDateSearch', '');
        setValue('closeDateSearch', '');
        setValue('invoiceDateSearch', '');
        setValue('invoiceAmountSearch', '');
        setValue('invoiceAmountLike', false);
        setValue('checkNumberSearch', '');
        setSelectedCreditCardType(undefined);
    };

    const handleSetIsCollapsed = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        if (props?.setIsCollapsed) {
            props.setIsCollapsed(collapsed);
        }
    };

    if (isCollapsed) {
        return (
            <div style={{ textAlign: 'center' }}>
                <Button
                    text="(+) Search Filters"
                    className="blue-hover-button"
                    style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                    onClick={() => handleSetIsCollapsed(false)}
                />
            </div>
        );
    }

    return (
        <Page className={'dlc-maxer-width'} style={{ justifyContent: 'center' }}>
            <Page.Section width={!!props?.size ? props.size : 12}>
                <Panel backgroundColor="#D3D3D3">
                    <Panel panelTitle="Credit Card Transaction Search">
                        <form>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0' }}>
                                <div style={{ width: '12%' }}>
                                    <Fieldset label={'Trans. No.'} style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox
                                            name="transactionNumberSearch"
                                            type="number"
                                            clearable={true}
                                            defaultVal={''}
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '20%', paddingLeft: '4px' }}>
                                    <Fieldset label={'Credit Card'} style={{ border: 0, padding: 0 }}>
                                        <Select
                                            isClearable={true}
                                            className="project-billing personnel-search-list"
                                            name="creditCardTypeSearch"
                                            placeholder={`Credit Card (${creditCardTypeOptions.length})`}
                                            options={creditCardTypeOptions}
                                            value={creditCardTypeOptions.find((x) => x.value === selectedCreditCardType) ?? null}
                                            onMenuOpen={() => setIsUserSearchingCreditCardType(true)}
                                            onMenuClose={() => setIsUserSearchingCreditCardType(false)}
                                            onBlur={() => setIsUserSearchingCreditCardType(false)}
                                            menuIsOpen={isUserSearchingCreditCardType}
                                            isSearchable={true}
                                            styles={customStyles}
                                            onChange={(opt: IOptionType | null) => setSelectedCreditCardType(opt?.value ?? undefined)}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '14%', paddingLeft: '4px' }}>
                                    <Fieldset label={'Check Date'} style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox
                                            register={register}
                                            name="checkDateSearch"
                                            type="date"
                                            clearable={true}
                                            setValue={setValue}
                                            defaultVal={''}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '14%', paddingLeft: '4px' }}>
                                    <Fieldset label={'Close Date'} style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox
                                            register={register}
                                            name="closeDateSearch"
                                            type="date"
                                            clearable={true}
                                            setValue={setValue}
                                            defaultVal={''}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '14%', paddingLeft: '4px' }}>
                                    <Fieldset label={'Invoice Date'} style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox
                                            register={register}
                                            name="invoiceDateSearch"
                                            type="date"
                                            clearable={true}
                                            setValue={setValue}
                                            defaultVal={''}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '15%', paddingLeft: '4px' }}>
                                    <Fieldset
                                        label={'Invoice Amount'}
                                        style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', border: '1px solid black', borderRadius: '4px', padding: '4px 6px' }}
                                    >
                                        <label style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '1px', margin: 0, marginRight: '4px' }}>
                                            Like
                                            <input
                                                type={'checkbox'}
                                                name={'invoiceAmountLike'}
                                                ref={register}
                                                value={'1'}
                                                defaultValue={'1'}
                                                defaultChecked={false}
                                            />
                                        </label>
                                        <RHFInputBox
                                            name="invoiceAmountSearch"
                                            type="number"
                                            clearable={true}
                                            defaultVal={''}
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            style={{ width: '100%', textAlign: 'right' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '10%', paddingLeft: '4px' }}>
                                    <Fieldset label={'Check No.'} style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox
                                            name="checkNumberSearch"
                                            type="number"
                                            clearable={true}
                                            defaultVal={''}
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <Button
                                    text={'Search'}
                                    className="blue-hover-button"
                                    style={{ width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', marginTop: '1.7rem', borderRadius: '5px' }}
                                    onClick={() => executeSearch()}
                                />
                                <Button
                                    text="(-) Minimize"
                                    className="blue-hover-button"
                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                    onClick={() => handleSetIsCollapsed(true)}
                                />
                                <Button
                                    text="Clear"
                                    className="blue-hover-button"
                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                    onClick={() => clearSearchFilters()}
                                />
                            </div>
                        </form>
                    </Panel>
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default CreditCardSearch;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/components/CreditCardSearch.tsx